/** Demo customer + reward catalogue for the usability-test build (Figma 2468:3720).
 *
 * This is the ONLY hardcoded data left in the screens, and it stands in for one thing: the
 * backend's `/customer` response (TechSpec B2), which does not serve the points shape yet. The
 * POS-side data — the products on the open order and the merchant's discount codes — is NOT here;
 * it is read live from the web-extension JS API (`pos_getCurrentAccount` /
 * `pos_getAvailableDiscounts`) via `lightspeed/pos-api.ts`, whose dev mock supplies demo values in
 * a browser. So swapping this file for `useCustomer()` is the only step left to be fully live.
 *
 * The points model (`0 / Points Algo`, Linear) is: earn `ticket CHF × 10`, and a reward costs
 * `ceil50(price CHF × 100)` — so 100 pts = 1 CHF of menu value, a ~10% give-back.
 */
import type { MarketplaceItem, MenuItem } from '../api/types'

/** The merchant's real Lightspeed items, transcribed from the trial back office
 * (pos-admin.trial.lsk.lightspeed.app → Menu → Liste des articles), NOT invented.
 *
 * `item_id` is the back office's **SKU** column. VERIFIED against a real device dump
 * (2026-07-29): a raw transaction line carries `itemSku: "13"` — matching this column exactly —
 * *alongside* a separate internal `itemIdentifier: "1399154316148985"`. So these ids are right,
 * but they only ever match a line's **`itemSku`** (see `matchingOrderLines`); comparing them to
 * `itemIdentifier` matches nothing. The dump also confirmed the prices below (Tacos 950,
 * Pan de yuca 500, Cappuccino 650).
 *
 * TWO ids per product, because the POS wants a different one for each job: `item_id` (the SKU) is
 * what a basket line reports as `itemSku` and what rewards MATCH on, while
 * `pos_item_identifier` is what `pos_addItemToCurrentAccount` accepts — proven on device, where the
 * SKU produced no line at all.
 *
 * Every `pos_item_identifier` here was read from a real ticket dump (2026-07-29, the merchant's
 * whole menu rung up in one go); none is guessed. Complete as of that dump — but only because
 * someone rang everything up by hand, which is exactly why this belongs in a synced menu (TechSpec
 * B4) rather than a file: the next product the merchant adds is invisible here until it appears on
 * a ticket.
 *
 * Stands in for the backend's synced menu (TechSpec B4) until that lands — the web-extension JS
 * API has no catalogue call, so there is nowhere live to read it from inside the extension.
 */
export const FIXTURE_MENU: MenuItem[] = [
  { item_id: '1', pos_item_identifier: '1399154316148907', name: 'Bière blanche', price_cents: 850 },
  { item_id: '2', pos_item_identifier: '1399154316148912', name: 'Blonde', price_cents: 700 },
  { item_id: '3', pos_item_identifier: '1399154316148917', name: 'Whiskey', price_cents: 1600 },
  { item_id: '4', pos_item_identifier: '1399154316148922', name: 'Mozza tomato', price_cents: 1200 },
  { item_id: '5', pos_item_identifier: '1399154316148927', name: 'Cheeseburger', price_cents: 1400 },
  { item_id: '6', pos_item_identifier: '1399154316148942', name: 'Embuscade', price_cents: 900 },
  { item_id: '7', pos_item_identifier: '1399154316148951', name: 'Tacos Lyonnais', price_cents: 950 },
  { item_id: '12', pos_item_identifier: '1399154316148981', name: 'Carbonara Maison', price_cents: 2500 },
  { item_id: '13', pos_item_identifier: '1399154316148985', name: 'Cappuccino', price_cents: 650 },
  { item_id: '14', pos_item_identifier: '1399154316148989', name: 'Pan de yuca', price_cents: 500 },
]

/** 2'300 pts, agreed with the PM for the research sessions.
 *
 * MUST stay in step with Malik's entry in `customer-search-fixtures.ts`: the search panel shows a
 * balance before you pick someone and this one is shown after, so a mismatch reads as the number
 * changing under the participant. They are two fixtures only because `/customer` does not serve a
 * points balance yet (TechSpec B2) — when it does, both go away and this cannot drift.
 *
 * At 2'300 only Carbonara Maison (2'500) is out of reach, which is deliberate: "Points
 * insuffisants" is a state the panel has to show, and a balance affording everything would hide it.
 * The design itself specifies 500 (exactly the first row claimable) — drop back to that to
 * screenshot faithfully. */
export const FIXTURE_CUSTOMER = {
  display_name: 'Lucas B.',
  points_balance: 2300,
}

/** Reward labels and point costs are the PM's (Figma 2468:3720); the linked `pos_item_id` is the
 * merchant's real SKU. Labels deliberately differ from the POS item names — a reward is
 * merchant-authored copy ("Bière Blonde"), the item is whatever the back office calls it
 * ("Blonde") — which is exactly why the link is an explicit mapping rather than a name match.
 *
 * ⚠ Two of the design's point costs do not reconcile with the back-office prices under Points
 * Algo §3 (`ceil50(price × 100)`):
 *     Bière Blonde  150 pts  vs  Blonde     CHF 7.00  → 700 pts
 *     Cappuccino    450 pts  vs  Cappuccino CHF 6.50  → 650 pts
 * The other four agree exactly. The design's numbers are kept here because they are what the PM
 * signed off and what the balance of 500 is tuned against — but one of the two has to give before
 * this is shown as real economics.
 *
 * Lasagne Calabrese has NO item in the back office at all, so it ships unlinked — which makes it
 * the reward-link case, for a genuine reason rather than a contrived one.
 *
 * Spread across all four claim paths (the mock basket in `lightspeed/pos-api.ts` is the other half
 * — change one without the other and the cases stop lining up):
 *   Lasagne Calabrese  no `pos_link`                   → reward-link flow
 *   Cappuccino         two matching lines on the ticket → "which line?" picker
 *   Pan de Yuca        linked, not on the ticket        → "Ajouter" only
 *   Bière Blonde       linked, one matching line        → straight through
 */
export const FIXTURE_ITEMS: MarketplaceItem[] = [
  { reward_id: 'biere-blonde', label: 'Bière Blonde', points_cost: 150, image_url: '/marketplace/biere-blonde.jpg', pos_link: { pos_item_id: '2', discount_code: 'BADGER_FREE' } },
  { reward_id: 'cappuccino', label: 'Cappuccino', points_cost: 450, image_url: '/marketplace/cappuccino.jpg', pos_link: { pos_item_id: '13', discount_code: 'BADGER_FREE' } },
  { reward_id: 'pan-de-yuca', label: 'Pan de Yuca', points_cost: 500, image_url: '/marketplace/pan-de-yuca.jpg', pos_link: { pos_item_id: '14', discount_code: 'BADGER_FREE' } },
  { reward_id: 'panini', label: 'Panini Moza Tomate', points_cost: 1200, image_url: '/marketplace/panini-moza-tomate.jpg', pos_link: { pos_item_id: '4', discount_code: 'BADGER_FREE' } },
  { reward_id: 'cheese-burger', label: 'Cheese Burger', points_cost: 1400, image_url: '/marketplace/cheese-burger.jpg', pos_link: { pos_item_id: '5', discount_code: 'BADGER_FREE' } },
  { reward_id: 'lasagne', label: 'Lasagne Calabrese', points_cost: 2000, image_url: '/marketplace/lasagne-calabrese.jpg' },
  { reward_id: 'carbonara', label: 'Carbonara Maison', points_cost: 2500, image_url: '/marketplace/carbonara-maison.jpg', pos_link: { pos_item_id: '12', discount_code: 'BADGER_FREE' } },
]
